import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const TransactionHistory = ({ tenantId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      loadTransactions();
    }
  }, [tenantId]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('pos_transactions')
        ?.select('*, user_profiles:cashier_id(full_name), member:member_id(full_name)')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Load transactions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'cancelled': return 'text-muted-foreground bg-muted/10';
      case 'refunded': return 'text-danger bg-danger/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getPaymentMethodLabel = (method) => {
    return method?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto mb-2" />
        <p className="text-muted-foreground">Loading transactions...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Recent Transactions</h3>
      </div>

      <div className="overflow-x-auto">
        {transactions?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No transactions yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Transaction #</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Date/Time</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cashier</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Member</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Payment</th>
                <th className="text-right p-3 text-sm font-medium text-muted-foreground">Amount</th>
                <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions?.map((transaction) => (
                <tr key={transaction?.id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-3">
                    <span className="text-sm font-medium text-foreground">
                      {transaction?.transaction_number}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">
                      {new Date(transaction?.created_at)?.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-foreground">
                      {transaction?.user_profiles?.full_name || 'Unknown'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-foreground">
                      {transaction?.member?.full_name || 'Guest'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">
                      {getPaymentMethodLabel(transaction?.payment_method)}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <span className="text-sm font-semibold text-foreground">
                      ${transaction?.total_amount?.toFixed(2)}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${getStatusColor(transaction?.status)}`}>
                      {transaction?.status?.charAt(0)?.toUpperCase() + transaction?.status?.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;